import { Key, degreePc } from './key';
import { PitchClass, mod, pcNumber, spellOnLetter } from './pitch';

export type TriadQuality = 'major' | 'minor' | 'diminished' | 'augmented';
export type SeventhType = 'dom7' | 'maj7' | 'min7' | 'hdim7' | 'dim7';
/** Tonic, predominant, dominant. */
export type HarmonicFunction = 'T' | 'PD' | 'D';

export interface ParsedRoman {
  text: string;
  accidental: number;
  degree: number;
  upper: boolean;
  triad: TriadQuality;
  seventh: SeventhType | null;
  inversion: 0 | 1 | 2 | 3;
  cadential: boolean;
  special?: 'N' | 'It' | 'Fr' | 'Ger';
  applied?: ParsedRoman;
}

export interface Chord {
  symbol: string;
  /** Key the symbol was read in. */
  key: Key;
  /** Key the chord belongs to (differs from `key` for applied chords such as V7/V). */
  localKey: Key;
  root: PitchClass;
  /** Root, third, fifth and (if present) seventh, spelled. Augmented sixths list bass first. */
  members: PitchClass[];
  pcs: number[];
  bass: PitchClass;
  inversion: 0 | 1 | 2 | 3;
  fn: HarmonicFunction;
  applied: boolean;
  triad: TriadQuality;
  seventh: SeventhType | null;
  cadential: boolean;
  special?: 'N' | 'It' | 'Fr' | 'Ger';
}

const NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const MAIN = /^(b|#|♭|♯)?(VII|VI|V|IV|III|II|I|vii|vi|v|iv|iii|ii|i)(o|°|ø|\+)?(7|65|43|42|2|64|63|6|53)?$/;

function parseMain(text: string): ParsedRoman {
  if (text === 'N6' || text === 'N' || text === 'N64') {
    return { text, accidental: -1, degree: 2, upper: true, triad: 'major', seventh: null, inversion: text === 'N6' ? 1 : text === 'N64' ? 2 : 0, cadential: false, special: 'N' };
  }
  if (text === 'Cad64') {
    return { text, accidental: 0, degree: 1, upper: true, triad: 'major', seventh: null, inversion: 2, cadential: true };
  }
  const aug = /^(It|Fr|Ger)(6|43|65)?$/.exec(text);
  if (aug) {
    return { text, accidental: 1, degree: 4, upper: false, triad: 'major', seventh: null, inversion: 0, cadential: false, special: aug[1] as 'It' | 'Fr' | 'Ger' };
  }
  const m = MAIN.exec(text);
  if (!m) throw new Error(`Cannot parse Roman numeral "${text}"`);
  const [, acc, numeral, symbol, figure = ''] = m;
  const upper = numeral === numeral.toUpperCase();
  const degree = NUMERALS.indexOf(numeral.toUpperCase()) + 1;
  const accidental = acc === 'b' || acc === '♭' ? -1 : acc === '#' || acc === '♯' ? 1 : 0;
  const triad: TriadQuality =
    symbol === 'o' || symbol === '°' || symbol === 'ø' ? 'diminished' : symbol === '+' ? 'augmented' : upper ? 'major' : 'minor';
  const hasSeventh = ['7', '65', '43', '42', '2'].includes(figure);
  const inversion = (({ '': 0, '53': 0, '7': 0, '6': 1, '63': 1, '65': 1, '64': 2, '43': 2, '42': 3, '2': 3 }) as Record<string, 0 | 1 | 2 | 3>)[figure];
  let seventh: SeventhType | null = null;
  if (hasSeventh) {
    if (symbol === 'o' || symbol === '°') seventh = 'dim7';
    else if (symbol === 'ø') seventh = 'hdim7';
    else if (triad === 'major') seventh = degree === 5 && accidental === 0 ? 'dom7' : 'maj7';
    else seventh = 'min7';
  }
  return { text, accidental, degree, upper, triad, seventh, inversion, cadential: false };
}

export function parseRoman(symbol: string): ParsedRoman {
  const s = symbol.trim();
  const slash = s.indexOf('/');
  if (slash > 0) {
    const main = parseMain(s.slice(0, slash));
    const applied = parseRoman(s.slice(slash + 1));
    if (main.seventh === 'maj7' && main.degree === 5) main.seventh = 'dom7';
    return { ...main, text: s, applied };
  }
  return parseMain(s);
}

const TRIAD_INTERVALS: Record<TriadQuality, number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
};
const SEVENTH_INTERVAL: Record<SeventhType, number> = { dom7: 10, maj7: 11, min7: 10, hdim7: 10, dim7: 9 };

function rootOf(p: ParsedRoman, key: Key): PitchClass {
  const raised = key.mode === 'minor' && (p.degree === 6 || p.degree === 7) && !p.upper;
  const base = degreePc(key, p.degree, raised);
  return { letter: base.letter, alter: base.alter + p.accidental };
}

function functionOf(p: ParsedRoman): HarmonicFunction {
  if (p.special) return 'PD';
  if (p.cadential) return 'D';
  if (p.applied) return 'D';
  if (p.accidental !== 0) return 'PD';
  if (p.degree === 5 || p.degree === 7) return 'D';
  if (p.degree === 2 || p.degree === 4) return 'PD';
  return 'T';
}

/** Turn a Roman numeral into spelled pitch classes in a key. */
export function realizeRoman(symbol: string, key: Key): Chord {
  const p = parseRoman(symbol);
  let localKey = key;
  if (p.applied) {
    const target = realizeRoman(p.applied.text, key);
    localKey = { tonic: target.root, mode: target.triad === 'major' || target.triad === 'augmented' ? 'major' : 'minor' };
  }

  if (p.special === 'It' || p.special === 'Fr' || p.special === 'Ger') {
    // Built on the key it leads into: Ger65/V in C is the German sixth of G (E♭ G B♭ C♯).
    const k = localKey;
    const b6 = degreePc(k, 6);
    const flat6 = k.mode === 'major' ? { letter: b6.letter, alter: b6.alter - 1 } : b6;
    const one = degreePc(k, 1);
    const sharp4Base = degreePc(k, 4);
    const sharp4 = { letter: sharp4Base.letter, alter: sharp4Base.alter + 1 };
    const members: PitchClass[] = [flat6, one];
    if (p.special === 'Fr') members.push(degreePc(k, 2));
    if (p.special === 'Ger') {
      const three = degreePc(k, 3);
      members.push(k.mode === 'major' ? { letter: three.letter, alter: three.alter - 1 } : three);
    }
    members.push(sharp4);
    return {
      symbol, key, localKey, root: sharp4, members, pcs: members.map(pcNumber), bass: flat6, inversion: 0,
      fn: 'PD', applied: false, triad: 'major', seventh: null, cadential: false, special: p.special,
    };
  }

  const root = rootOf(p, localKey);
  const rootNum = pcNumber(root);
  const intervals = [...TRIAD_INTERVALS[p.triad]];
  if (p.seventh) intervals.push(SEVENTH_INTERVAL[p.seventh]);
  const members = intervals.map((iv, i) => spellOnLetter(root.letter + 2 * i, rootNum + iv));
  const inversion = (Math.min(p.inversion, members.length - 1) as 0 | 1 | 2 | 3);
  return {
    symbol,
    key,
    localKey,
    root,
    members,
    pcs: members.map(pcNumber),
    bass: members[inversion],
    inversion,
    fn: functionOf(p),
    applied: !!p.applied,
    triad: p.triad,
    seventh: p.seventh,
    cadential: p.cadential,
    special: p.special,
  };
}

const SUP: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
const SUB: Record<string, string> = { '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉' };

/** Typeset a Roman numeral for display: "viio7/V" → "vii°⁷/V", "V65" → "V⁶₅". */
export function formatRoman(symbol: string, short = false): string {
  if (symbol === 'Cad64') return short ? 'I⁶₄' : 'I⁶₄ (cad.)';
  return symbol
    .split('/')
    .map((part) => {
      const m = /^(b|#)?([ivxIVX]+|N|It|Fr|Ger)(o|°|ø|\+)?(\d*)$/.exec(part);
      if (!m) return part;
      const [, acc = '', body, sym = '', fig] = m;
      const accU = acc === 'b' ? '♭' : acc === '#' ? '♯' : '';
      const symU = sym === 'o' ? '°' : sym;
      let figU = '';
      if (fig.length === 2 && fig !== '53') figU = SUP[fig[0]] + SUB[fig[1]];
      else if (fig.length === 1) figU = SUP[fig];
      return accU + body + symU + figU;
    })
    .join('/');
}

/** Identify a sounding pitch-class set against a list of candidate symbols in a key. */
export function identifyChord(pcs: number[], key: Key, candidates: string[]): { symbol: string; exact: boolean } | null {
  const set = new Set(pcs.map((n) => mod(n, 12)));
  if (set.size < 2) return null;
  let best: { symbol: string; score: number; exact: boolean } | null = null;
  for (const symbol of candidates) {
    const chord = realizeRoman(symbol, key);
    const chordSet = new Set(chord.pcs);
    let hit = 0;
    let extra = 0;
    set.forEach((pc) => (chordSet.has(pc) ? hit++ : extra++));
    const missing = chordSet.size - hit;
    const score = hit * 2 - extra * 3 - missing;
    const exact = extra === 0 && missing === 0;
    if (extra === 0 && hit >= Math.min(3, chordSet.size) && (!best || score > best.score)) best = { symbol, score, exact };
  }
  return best ? { symbol: best.symbol, exact: best.exact } : null;
}
